import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  observationAnswerSetId,
  recordedObservationAnswers,
} from '@seen/core/observationReferences'
import { referenceStringToId } from '@seen/core/references'
import { Button, Separator } from '@seen/ui'
import { IconTrash } from '@tabler/icons-react'
import { orderBy } from 'lodash-es'
import { type JSX, useMemo } from 'react'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { ResourceFieldFormBatch } from '#src/components/core/form/ResourceFieldFormBatch.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { ObservationActivity } from '#src/components/rich-text-editor/ObservationNode/ObservationActivity.tsx'
import { ObservationAnswerSetNote } from '#src/components/rich-text-editor/ObservationNode/ObservationAnswerSetNote.tsx'
import { ObservationHistory } from '#src/components/rich-text-editor/ObservationNode/ObservationHistory.tsx'
import { shownValue } from '#src/components/rich-text-editor/ObservationNode/observationLastValue.ts'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useClearObservationAnswer } from '#src/components/rich-text-editor/ObservationNode/useClearObservationAnswer.ts'
import {
  fromDraft,
  toDraft,
} from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useGroupedObservationHistory } from '#src/hooks/useObservationHistory.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface ObservationAnswerSetDialogProps {
  attributes: ObservationNodeAttributes
  // Writes the node's attributes. The answers held in the note (`drafts`)
  // are edited through it.
  updateAttributes?: (attributes: Partial<ObservationNodeAttributes>) => void
  onCleared?: () => void
  initialFocus?: 'note'
}

// The details of one multi-select capture: its date, note, answers, earlier
// captures, and the activity of each answer.
export function ObservationAnswerSetDialog({
  attributes,
  updateAttributes,
  onCleared,
  initialFocus,
}: ObservationAnswerSetDialogProps): JSX.Element {
  const practitioner = useCurrentPractitioner()
  const held = useMemo(
    () => (attributes.drafts ?? []).map(fromDraft),
    [attributes.drafts]
  )
  const holdsDrafts = held.length > 0
  const changeDrafts = (
    mutateFn: (observation: ExtendedObservation) => void
  ): void => {
    updateAttributes?.({
      drafts: held.map((observation) => {
        const next = fromDraft(toDraft(observation))
        mutateFn(next)
        return toDraft(next)
      }),
    })
  }
  const answers = recordedObservationAnswers(attributes.references)
  const ids = answers.map((answer) => referenceStringToId(answer.reference))
  const { data: observations } = useSearch(
    ExtendedObservation,
    ids.length > 0 ? { _id: ids.join(',') } : undefined,
    { silentErrors: true }
  )
  const recorded = holdsDrafts ? held : (observations ?? [])
  const primary = recorded[0]
  const coding = { system: attributes.system, code: attributes.code }
  const exceptReferences = useMemo(
    () =>
      recordedObservationAnswers(attributes.references).map(
        (answer) => answer.reference
      ),
    [attributes.references]
  )
  const history = useGroupedObservationHistory({
    participant: primary?.subject,
    coding,
    exceptReferences,
    exceptGroupId:
      attributes.groupId ??
      (primary ? observationAnswerSetId(primary.identifiers()) : null),
    limit: 5,
  }).flatMap((group) => group.members)
  const clearAnswer = useClearObservationAnswer()
  const scaleType = attributes.scaleType ?? 'Nom'
  const summary = orderBy(
    recorded,
    (observation) => shownValue(observation) ?? ''
  )
    .map((observation) => shownValue(observation))
    .filter((label) => !!label)
    .join(', ')

  return (
    <DialogContent className="gap-6">
      <div className="flex items-center justify-between gap-2 pt-1 pr-10 pb-4">
        {primary && holdsDrafts ? (
          <DatePicker
            value={primary.effectiveDateTime ?? null}
            onChange={(value) => {
              if (value) {
                changeDrafts((record) => {
                  record.effectiveDateTime = value
                })
              }
            }}
            disabled={!updateAttributes}
            placeholder="Effective Date"
            className="h-10"
          />
        ) : primary ? (
          <ResourceFieldFormBatch
            instance={primary}
            valueFn={(observation) => observation.effectiveDateTime}
            builderFn={(builder, value) => {
              invariant(value, 'Effective date is required')
              for (const observation of recorded) {
                builder.patch(observation, (record) => {
                  record.effectiveDateTime = value
                })
              }
            }}
            render={({ field }) => (
              <DatePicker
                {...field}
                placeholder="Effective Date"
                className="h-10"
              />
            )}
          />
        ) : (
          <span />
        )}
        {onCleared && recorded.length > 0 && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete"
            onClick={() => {
              if (holdsDrafts) {
                updateAttributes?.({ drafts: null })
                onCleared()
                return
              }
              clearAnswer({
                observations: recorded,
                label: attributes.codingDisplay,
                onCleared,
              })
            }}
          >
            <IconTrash className="size-4" />
          </Button>
        )}
      </div>
      <DialogTitle>{attributes.codingDisplay}</DialogTitle>
      {summary && <p className="text-sm">{summary}</p>}
      {recorded.length > 0 && (
        <ObservationAnswerSetNote
          observations={recorded}
          autofocus={initialFocus === 'note'}
          onDraftNote={
            holdsDrafts
              ? (value) =>
                  changeDrafts((record) => {
                    record.setNoteFirst(
                      value || undefined,
                      practitioner.reference
                    )
                    record.performerPractitioner = practitioner.reference
                  })
              : undefined
          }
          className="max-w-(--container-prose) grow"
        />
      )}
      {history.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <h5>Previous</h5>
            <ObservationHistory observations={history} coding={coding} />
          </div>
        </>
      )}
      {!holdsDrafts && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <h5>Activity</h5>
            <ActivityList>
              {recorded.map((observation) => (
                <ObservationActivity
                  key={observation.reference.reference}
                  codingDisplay={
                    shownValue(observation) ?? attributes.codingDisplay
                  }
                  observationId={referenceStringToId(
                    observation.reference.reference
                  )}
                  scaleType={scaleType}
                />
              ))}
            </ActivityList>
          </div>
        </>
      )}
    </DialogContent>
  )
}
